import { Icon } from '@common/index';
import React from 'react';
import styles from './Link.module.scss';

type LinkItem = {
    title: string;
    url: string;
    description?: string;
    rank?: number;
    displayed_link?: string;
    favicon?: string;
};

export type SearchMetadata = {
    id?: string;
    status?: string;
    json_endpoint?: string;
    created_at?: string;
    processed_at?: string;
    google_url?: string;
    raw_html_file?: string;
    total_time_taken?: number;
};

export interface SearchOutput {
    results: LinkItem[];
    total_results?: number;
    search_metadata?: SearchMetadata;
}

export interface LinkListProps {
    output: SearchOutput;
}

const getDomainFromUrl = (url: string) => {
    try {
        const domain = new URL(url).hostname;
        return domain.replace('www.', '');
    } catch {
        return url;
    }
};

const RankBadge = ({ rank }: { rank: number }) => {
    return (
        <div className={styles.rankBadge}>
            #
            {rank}
        </div>
    );
};

export const SourcesSummary: React.FC<{ links: LinkItem[] }> = ({ links }) => {
    const uniqueDomains = new Set(links.map(link => getDomainFromUrl(link.url)));
    const websiteCount = uniqueDomains.size;
    
    return (
        <div className={styles.summary}>
            <div className={styles.favicons}>
                {links.slice(0, 4).map((link) => (
                    <div key={link.url} className={styles.faviconWrapper}>
                        {link.favicon ? (
                            <img 
                                className={styles.summaryFavicon} 
                                src={link.favicon} 
                                alt=""
                                onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                }}
                            />
                        ) : (
                            <div className={styles.faviconPlaceholder}>
                                <Icon name="link" size={12} />
                            </div>
                        )}
                    </div>
                ))}
            </div>
            <span className={styles.summaryText}>
                Sourced from 
                {' '}
                {websiteCount}
                + websites
            </span>
        </div>
    );
};

export const LinkList: React.FC<LinkListProps> = ({ output }) => {
    const links = output?.results || [];
    if (!links || links.length === 0) return null;

    return (
        <div className={styles.wrapper}>
            <SourcesSummary links={links} />
            {links.map((link, index) => {
                const domain = getDomainFromUrl(link.url);
                return (
                    <React.Fragment key={link.url}>
                        <div className={styles.divider} />
                        <div key={link.url || index} className={styles.card}>
                            <a
                                className={styles.main}
                                href={link.url}
                                target="_blank"
                                rel="noreferrer"
                                aria-label={link.title || ''}
                            >
                                <div className={styles.iconBox}>
                                    {link.favicon ? (
                                        <img 
                                            className={styles.favicon} 
                                            src={link.favicon} 
                                            alt={`${domain} favicon`}
                                            onError={(e) => {
                                                e.currentTarget.style.display = 'none';
                                                e.currentTarget.nextElementSibling?.classList.remove(styles.hidden);
                                            }}
                                        />
                                    ) : null}
                                </div>
                                <div className={styles.content}>
                                    <div className={styles.header}>
                                        <div
                                            className={styles.title}
                                            title={link.title || ''}
                                        >
                                            {link.title}
                                        </div>
                                    </div>
                                    <div className={styles.url}>
                                        {link.displayed_link || domain}
                                    </div>
                                    {link.description ? (
                                        <div className={styles.description}>
                                            {link.description}
                                        </div>
                                    ) : null}
                                </div>
                            </a>
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default LinkList;